import React, { useState, useMemo, useLayoutEffect } from "react";

import { ToastNotification, showToast } from "@acadia-ui/toast";
import DataTable, { TD_TYPE, ColumnTemplate } from "@acadia-ui/datatable";
import { DataProvider } from "@acadia-ui/theme";
import Button, { BUTTON_DISPLAY, BUTTON_SIZE } from "@acadia-ui/button";
import Popover from "@acadia-ui/popover";
import Loading from "@acadia-ui/loading";
import Tooltip, { TOOLTIP_POSITION } from "@acadia-ui/tooltip";
import AppSDK from "@growthos/ui-app-sdk";
import Status, { STATUS_TYPE } from "@acadia-ui/status";
import Toggle from "@acadia-ui/toggle";

import { PLATFORM_MAP, ID_NAME_MAP } from "./constants";
import MasterCampaignsStore from "../../Flux/Stores/MasterCampaignsStore";
import { putCampaignUniqueID, holdMasterCampaignUniqueID, fetchActivationHeaders } from "../../Flux/Actions/MasterCampaignActions";
import BookFiltersStore from "../../Flux/Stores/BookFiltersStore";
import { putFilterData } from "../../Flux/Actions/BookFilterActions";
import { TaxonomyCellRenderer } from "../ViewMasterCampaigns/utils";

export const ComparisonGrid = ({
	columns,
	data,
	error,
	loading,
	hasValidDropdowns,
	setRefreshFlag,
	refreshFlag,
	showAllOSColumns,
	setShowAllOSColumns,
	downloadReport,
	legendPlatforms,
	type,
	campaignID,
	masterCampaignID,
	hierarchyLevelID,
	platformId
}) => {
	const [dp, setDp] = useState();
	const [selectedRows, setSelectedRows] = useState([]);

	// layout effect that updates the error outlines when the selected columns update
	useLayoutEffect(() => {
		const collection = document.getElementsByClassName("csqa-validationError");
		Array.from(collection).forEach((element) => {
			element?.classList?.remove("csqa-validationError");
		});

		const stickyCollection = document.getElementsByClassName("csqa-sticky-validationError");
		Array.from(stickyCollection).forEach((element) => {
			element?.classList?.remove("csqa-sticky-validationError");
		});
	}, [columns]);

	// if no columns, send [] to the DataTable's DataProvider to force the emptyState component to show.
	useLayoutEffect(() => {
		if (!loading) {
			setDp(new DataProvider({ data: columns?.length > 0 ? data : [] }));
		}
		if (loading) {
			// reset selected rows if the data changes
			setSelectedRows([]);
		}
	}, [data, loading, columns]);

	// memoized component shown when there is no/invalid data
	const emptyState = useMemo(() => {
		if (error) {
			return (
				<div className="csqa-center">
					<Status type={STATUS_TYPE.ERROR} title="An error Occurred." />
					<span style={{ marginLeft: 5 }}>An error occurred while fetching data for the selected campaign.</span>
				</div>
			);
		}
		if (loading) {
			return (
				<div className="csqa-center">
					<Loading />
				</div>
			);
		}
		if (data?.length == 0) {
			if (hasValidDropdowns) {
				return (
					<div className="csqa-center">
						<Status type={STATUS_TYPE.REVISIONNEEDED} title="No data to show" />
						<span style={{ marginLeft: 5 }}>There is no data available to compare.</span>
					</div>
				);
			}
			return (
				<div className="csqa-center">
					<Status type={STATUS_TYPE.REVISIONNEEDED} title="No data to show" />
					<span style={{ marginLeft: 5 }}>Please select a platform and campaign to compare values.</span>
				</div>
			);
		}
		if (hasValidDropdowns && data?.length > 0 && columns?.length == 0) {
			return (
				<div className="csqa-center">
					<Status type={STATUS_TYPE.SUCCESS} title="No Platform Discrepencies" />
					<span style={{ marginLeft: 5 }}>There are no platform discrepencies.</span>
				</div>
			);
		}
		return <></>;
	}, [loading, error, data, columns, hasValidDropdowns]);

	const numberSelected = useMemo(() => {
		return selectedRows.length;
	}, [selectedRows]);

	const navigate = () => {
		let taxonomy = null;
		let dataType = null;
		const selectedRowID = selectedRows[0];
		const {
			// this comment exists to prevent eslint from making this unreadable.
			placementManager_CampaignName,
			placementManager_PlacementName,
			placementManager_PackageName,
			externalPlatform_gcm_PlacementName,
			externalPlatform_gcm_PackageName,
			externalPlatform_prisma_PlacementName,
			externalPlatform_prisma_PackageName
		} = data.find((row) => row.id === selectedRowID) || {};
		const xor = (...options) => options.filter((opt) => opt !== null && opt !== undefined).length === 1;
		switch (type) {
			case "Campaign":
				if (placementManager_CampaignName && xor(placementManager_CampaignName, placementManager_PlacementName, placementManager_PackageName)) {
					taxonomy = placementManager_CampaignName;
					dataType = type;
				}
				break;
			case "PackagePlacement":
				if (placementManager_PlacementName) {
					taxonomy = placementManager_PlacementName;
					dataType = "Placement";
				} else if (placementManager_PackageName && !placementManager_PlacementName) {
					taxonomy = placementManager_PackageName;
					dataType = "Package";
				} else if (externalPlatform_gcm_PlacementName) {
					taxonomy = externalPlatform_gcm_PlacementName;
					dataType = "Placement";
				} else if (externalPlatform_gcm_PackageName && !externalPlatform_gcm_PlacementName) {
					taxonomy = externalPlatform_gcm_PackageName;
					dataType = "Package";
				} else if (externalPlatform_prisma_PlacementName) {
					taxonomy = externalPlatform_prisma_PlacementName;
					dataType = "Placement";
				} else if (externalPlatform_prisma_PackageName && !externalPlatform_prisma_PlacementName) {
					taxonomy = externalPlatform_prisma_PackageName;
					dataType = "Package";
				}
				break;
		}

		if (taxonomy !== null) {
			if (type === "PackagePlacement") {
				handleWorkbookNavigate(campaignID, taxonomy, dataType);
			} else {
				handleHomepageNavigate(taxonomy);
			}
		}
	};

	const handleWorkbookNavigate = (campaignID, taxonomyString, dataType) => {
		try {
			const URL =
				window.location.origin +
				`/Workbook/?hierarchyLevelID=${hierarchyLevelID}&platformID=${platformId}&campaignID=${campaignID}&masterCampaignID=${masterCampaignID}&${dataType}=${taxonomyString}`;
			window.open(URL, "_blank");
		} catch (e) {
			showToast("crossPlatformQABanners", <ToastNotification type="error" title="Matching campaign was not found!" />);
		}
	};

	const handleHomepageNavigate = (taxonomy) => {
		try {
			const url = window.location.origin + `/?taxonomy=${taxonomy}`;
			window.open(url, "_blank");
		} catch (e) {
			showToast("crossPlatformQABanners", <ToastNotification type="error" title="Matching campaign was not found!" />);
		}
	};

	// render the Data Table
	return (
		<>
			<div className="csqa-legend">
				Platform Legend:
				{legendPlatforms.map((platform) => (
					<>
						<div className={`${PLATFORM_MAP?.[ID_NAME_MAP[platform.value.toString()]]?.legendClass}`} /> {PLATFORM_MAP?.[ID_NAME_MAP[platform.value.toString()]]?.label}
					</>
				))}
			</div>
			<DataTable
				onCheckboxChange={(evt, chkStates, node) => {
					const nodes = node.value?.includes("select-all") ? node.children.loadedNodes : node.parent.children.loadedNodes;
					const selectedNodes = nodes?.filter(({ selectionState }) => selectionState)?.map(({ renderProps }) => renderProps?.value) || [];
					setSelectedRows(selectedNodes);
				}}
				data={dp}
				bindKey="id"
				emptyState={emptyState}
				hasVirtualScrolling={false}
				loadingColumnCount={20}
				actionBarProps={{
					showEditMode: false,
					actions: [
						<Tooltip
							// this comment exists to prevent eslint from making this unreadable.
							className="editTooltip"
							title="Selecting edit will open a new tab filtered to the selected line"
							position={TOOLTIP_POSITION.BOTTOM_START}
						>
							<Button
								// this comment exists to prevent eslint from making this unreadable.
								disabled={numberSelected !== 1}
								display={BUTTON_DISPLAY.ICON_BORDERED}
								size={BUTTON_SIZE.SMALL}
								icon="icon-launch"
								onClick={navigate}
								data-tracking-id-alt="comparison-grid-edit-selected-row"
							>
								Edit
							</Button>
						</Tooltip>
					],
					defaultModeActions: [
						<>
							{legendPlatforms.length > 0 && (
								<>
									<Tooltip title={"Refresh"} position={TOOLTIP_POSITION.BOTTOM}>
										<Button
											// this comment exists to prevent eslint from making this unreadable.
											display={BUTTON_DISPLAY.ICON_BORDERED}
											size={BUTTON_SIZE.SMALL}
											icon="icon-sync"
											onClick={() => setRefreshFlag(!refreshFlag)}
											data-tracking-id-alt="refresh-comparison-grid"
										>
											Refresh
										</Button>
									</Tooltip>
									<Tooltip title={"Download"} position={TOOLTIP_POSITION.BOTTOM_END}>
										<Button
											// this comment exists to prevent eslint from making this unreadable.
											display={BUTTON_DISPLAY.ICON_BORDERED}
											size={BUTTON_SIZE.SMALL}
											icon="icon-download"
											onClick={downloadReport}
											data-tracking-id-alt="download-comparison-grid"
										>
											Download
										</Button>
									</Tooltip>
									<Toggle
										// this comment exists to prevent eslint from making this unreadable.
										id={`${type}-csqa-error-toggle`}
										checked={!showAllOSColumns}
										onChange={(e, v) => {
											setShowAllOSColumns(!v);
										}}
									>
										{"Show Errors Only"}
									</Toggle>
								</>
							)}
						</>
					]
				}}
			>
				{[
					dp?.data?.length > 0 ? (
						<ColumnTemplate sticky={true} bind="id" type={TD_TYPE.CHECKBOX} className="checkboxColumn" style={{ padding: "16px 0 0 16px !important" }}>
							Checkboxes
						</ColumnTemplate>
					) : (
						<></>
					),
					...columns.map(({ label, ...rest }) => (!loading ? <ColumnTemplate {...rest}>{label}</ColumnTemplate> : <></>))
				]}
			</DataTable>
		</>
	);
};
